import { API } from "../api.js";
import { enterDashboard, optionsCache } from "./shell.js";
import { $, esc, fmtMoney, opt } from "../ui/format.js";
import { toast } from "../ui/modals.js";
import { renderChallengeBanner } from "../views/core/overview.js";

let selectedTier = null;

let selectedDifficulty = "Standard";

let selectedScenario = "Custom Founding";

let selectedChallenge = null; // null = no challenge (normal sandbox), otherwise a challenge id

function populateOnboardingDropdowns() {
  $("f-country").innerHTML = opt(optionsCache.countries);
  $("f-funding").innerHTML = opt(optionsCache.funding_types);
  $("f-scale").innerHTML = opt(optionsCache.scales);

  const qs = $("qs-industry");
  if (qs) qs.innerHTML = (optionsCache.industries || []).map((ind) => `<option value="${esc(ind.name)}">${esc(ind.name)}</option>`).join("");
  const industrySelect = $("f-industry");
  const industryList = optionsCache.industries || [];
  industrySelect.innerHTML = industryList.map((ind) =>
    `<option value="${esc(ind.name)}" title="${esc(ind.description)}">${esc(ind.name)}</option>`
  ).join("");
  const industryDescEl = document.getElementById("f-industry-desc");
  const updateIndustryDesc = () => {
    if (!industryDescEl) return;
    const picked = industryList.find((i) => i.name === industrySelect.value);
    industryDescEl.textContent = picked ? picked.description : "";
  };
  industrySelect.addEventListener("change", updateIndustryDesc);
  updateIndustryDesc();

  const tierGrid = $("tier-grid");
  tierGrid.innerHTML = optionsCache.capital_tiers.map((t, i) => `
    <div class="tier-card ${i === 0 ? "selected" : ""}" data-label="${t.label}">
      <div class="tier-name">${t.label.replace(/\s*\(.*\)/, "")}</div>
      <div class="tier-amount">${fmtMoney(t.capital)}</div>
      <div class="tier-debt">${t.debt > 0 ? "+" + fmtMoney(t.debt) + " debt" : "no debt"}</div>
    </div>
  `).join("");
  selectedTier = optionsCache.capital_tiers[0].label;
  tierGrid.querySelectorAll(".tier-card").forEach((card) => {
    card.addEventListener("click", () => {
      tierGrid.querySelectorAll(".tier-card").forEach((c) => c.classList.remove("selected"));
      card.classList.add("selected");
      selectedTier = card.dataset.label;
    });
  });

  const difficultyGrid = $("difficulty-grid");
  difficultyGrid.innerHTML = optionsCache.difficulty_presets.map((d, i) => `
    <div class="tier-card mode-card ${d.label === "Standard" ? "selected" : ""}" data-label="${d.label}">
      <div class="tier-name">${d.label}</div>
      <div class="tier-desc">${d.description}</div>
    </div>
  `).join("");
  selectedDifficulty = "Standard";
  difficultyGrid.querySelectorAll(".tier-card").forEach((card) => {
    card.addEventListener("click", () => {
      difficultyGrid.querySelectorAll(".tier-card").forEach((c) => c.classList.remove("selected"));
      card.classList.add("selected");
      selectedDifficulty = card.dataset.label;
    });
  });

  const scenarioGrid = $("scenario-grid");
  scenarioGrid.innerHTML = optionsCache.scenario_presets.map((s) => `
    <div class="tier-card mode-card ${s.label === "Custom Founding" ? "selected" : ""}" data-label="${s.label}">
      <div class="tier-name">${s.label}</div>
      <div class="tier-desc">${s.description}</div>
    </div>
  `).join("");
  selectedScenario = "Custom Founding";
  scenarioGrid.querySelectorAll(".tier-card").forEach((card) => {
    card.addEventListener("click", () => {
      scenarioGrid.querySelectorAll(".tier-card").forEach((c) => c.classList.remove("selected"));
      card.classList.add("selected");
      selectedScenario = card.dataset.label;
    });
  });

  const challengeGrid = $("challenge-grid");
  const noChallengeCard = `
    <div class="tier-card mode-card selected" data-id="">
      <div class="tier-name">None — Sandbox</div>
      <div class="tier-desc">No win condition. Keep your Scenario pick above and play open-ended.</div>
    </div>`;
  challengeGrid.innerHTML = noChallengeCard + optionsCache.challenge_presets.map((ch) => `
    <div class="tier-card mode-card" data-id="${ch.id}" data-force-tier="${ch.force_capital_tier || ""}">
      <div class="tier-name">${esc(ch.name)}</div>
      <div class="tier-desc">${esc(ch.description)}</div>
      <div class="tier-debt">Deadline: month ${ch.deadline_months}</div>
    </div>
  `).join("");
  selectedChallenge = null;
  challengeGrid.querySelectorAll(".tier-card").forEach((card) => {
    card.addEventListener("click", () => {
      challengeGrid.querySelectorAll(".tier-card").forEach((c) => c.classList.remove("selected"));
      card.classList.add("selected");
      selectedChallenge = card.dataset.id || null;

      // A challenge with a forced capital tier locks that choice visually —
      // the backend enforces this regardless, this just avoids a confusing
      // "I picked X but got Y" moment for the player.
      const forcedTier = card.dataset.forceTier;
      tierGrid.querySelectorAll(".tier-card").forEach((t) => t.classList.remove("locked"));
      if (forcedTier) {
        tierGrid.querySelectorAll(".tier-card").forEach((t) => {
          t.classList.toggle("selected", t.dataset.label === forcedTier);
          if (t.dataset.label !== forcedTier) t.classList.add("locked");
        });
        selectedTier = forcedTier;
      }
    });
  });
}

/** Reset save: a deliberate, typed confirmation (a stray click can't wipe a
 *  company), then the same wipe as New Game and back to founding. */
function openResetSaveModal() {
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal reset-modal">
      <h3>Reset your save?</h3>
      <p>This permanently deletes <b>${esc((window.__company && window.__company.name) || "your company")}</b>
        and all its history, and the game starts again from founding. Named save slots are kept, so you can still load one later.</p>
      <div class="field"><label>Type RESET to confirm</label><input id="reset-confirm" autocomplete="off" placeholder="RESET"/></div>
      <div class="modal-actions">
        <button id="reset-cancel">Cancel</button>
        <button class="danger" id="reset-go" disabled>Reset and start over</button>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const input = backdrop.querySelector("#reset-confirm");
  const go = backdrop.querySelector("#reset-go");
  input.addEventListener("input", () => { go.disabled = input.value.trim().toUpperCase() !== "RESET"; });
  backdrop.querySelector("#reset-cancel").addEventListener("click", () => backdrop.remove());
  go.addEventListener("click", async () => {
    go.disabled = true;
    backdrop.remove();
    await startNewGame(null);
    toast("Save reset. Found your new company.");
  });
  input.focus();
}

// ---------------------------------------------------------------- onboarding

function buildFoundingPayload() {
  return {
    hq_country: $("f-country").value,
    industry: $("f-industry").value,
    funding_type: $("f-funding").value,
    capital_tier: selectedTier,
    additional_debt: parseFloat($("f-debt").value) || 0,
    scale: $("f-scale").value,
    difficulty: selectedDifficulty,
    scenario: selectedScenario,
    challenge: selectedChallenge,
  };
}

function renderFoundingPreview(data) {
  const monthRows = data.months.map((m) => `
    <tr>
      <td>Month ${m.month}</td>
      <td>${fmtMoney(m.revenue)}</td>
      <td style="color:${m.net_income >= 0 ? "var(--sage)" : "var(--oxblood)"};">${fmtMoney(m.net_income)}</td>
      <td>${fmtMoney(m.cash)}</td>
    </tr>
  `).join("");
  const tierNote = data.capital_tier_applied !== selectedTier
    ? `<div class="preview-note">Capital tier forced to <strong>${esc(data.capital_tier_applied)}</strong> by your Challenge pick.</div>` : "";

  return `
    <div class="preview-panel">
      ${data.challenge ? renderChallengeBanner(data.challenge) : ""}
      <div class="preview-starting">
        <div><span class="preview-label">You put in</span><span class="preview-value">${fmtMoney(data.starting_capital)}</span></div>
        <div><span class="preview-label">+ Starting loan</span><span class="preview-value">${fmtMoney(data.starting_debt)}</span></div>
        <div><span class="preview-label">− Day-one equipment</span><span class="preview-value">${fmtMoney(data.starting_equipment || 0)}</span></div>
        <div><span class="preview-label">= Cash on day one</span><span class="preview-value">${fmtMoney(data.starting_cash != null ? data.starting_cash : data.starting_capital)}</span></div>
      </div>
      <div class="preview-note">Your industry puts ${data.equipment_pct || 0}% of its starting money into equipment, so that part isn't cash, but the company still owns it.
        Valuation: <strong>not valued yet</strong>. A new company is worth its net assets (${fmtMoney(data.starting_net_assets != null ? data.starting_net_assets : data.starting_capital)}); the market values it from your first month of sales, and the value builds over your first year.</div>
      ${tierNote}
      <table class="preview-table">
        <thead><tr><th>Month</th><th>Revenue</th><th>Net Income</th><th>Cash</th></tr></thead>
        <tbody>${monthRows}</tbody>
      </table>
      <div class="preview-note">First 3 months, simulated on a throwaway copy — actual results will vary once you're making decisions.</div>
    </div>
  `;
}

$("preview-btn").addEventListener("click", async () => {
  const btn = $("preview-btn");
  const resultsEl = $("preview-results");
  if (!$("f-country").value || !$("f-industry").value || !$("f-funding").value || !$("f-scale").value || !selectedTier) {
    resultsEl.innerHTML = `<div class="preview-note" style="color:var(--oxblood);">Fill in the fields above first.</div>`;
    return;
  }
  btn.disabled = true;
  btn.textContent = "Running preview…";
  resultsEl.innerHTML = "";
  try {
    const res = await fetch(`${API}/founding-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildFoundingPayload()),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Could not run preview.");
    }
    const data = await res.json();
    resultsEl.innerHTML = renderFoundingPreview(data);
  } catch (err) {
    resultsEl.innerHTML = `<div class="preview-note" style="color:var(--oxblood);">${esc(err.message)}</div>`;
  } finally {
    btn.disabled = false;
    btn.textContent = "Preview my starting position →";
  }
});

// ---------------------------------------------------------------- New Game

async function startNewGame(confirmMsg) {
  if (confirmMsg && !confirm(confirmMsg)) return;
  await fetch(`${API}/new-game`, { method: "POST" });
  $("dashboard-screen").classList.add("hidden");
  $("gameover-screen").classList.add("hidden");
  $("onboard-error").classList.add("hidden");
  $("onboard-new-game").classList.add("hidden");
  $("onboarding-screen").classList.remove("hidden");
}

// ---------------------------------------------------------------- onboarding

$("onboarding-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const errEl = $("onboard-error");
  errEl.classList.add("hidden");
  $("onboard-new-game").classList.add("hidden");
  $("onboard-submit").disabled = true;

  const payload = Object.assign({ name: $("f-name").value.trim(), founder_name: ($("f-founder") && $("f-founder").value.trim()) || null }, buildFoundingPayload());

  try {
    const res = await fetch(`${API}/company`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Could not found company.");
    }
    $("onboarding-screen").classList.add("hidden");
    await enterDashboard();
  } catch (err) {
    errEl.textContent = err.message;
    errEl.classList.remove("hidden");
    if (/already exists/i.test(err.message)) $("onboard-new-game").classList.remove("hidden");
  } finally {
    $("onboard-submit").disabled = false;
  }
});

// ---------------------------------------------------------------- quick start (Phase 10: first launch in under 3 minutes)

const QS_NAMES = ["Northwind", "Brightpath", "Ironleaf", "Bluecrest", "Silverline", "Redwood", "Starling", "Keystone", "Lumen", "Harbor"];

/** One click from the founding screen to the first launch: defaults for everything but the industry. */
async function quickStart() {
  const btn = $("qs-go");
  btn.disabled = true;
  const industry = $("qs-industry").value;
  const o = optionsCache;
  const label = (v) => (typeof v === "string" ? v : v.label);
  const tiers = o.capital_tiers || [];
  const payload = {
    name: `${QS_NAMES[Math.floor(Math.random() * QS_NAMES.length)]} ${industry.split(/[ (&]/)[0]}`,
    founder_name: ($("qs-founder") && $("qs-founder").value.trim()) || null,
    hq_country: (o.countries || []).includes("United States") ? "United States" : label((o.countries || [])[0]),
    industry, funding_type: label((o.funding_types || [])[0]),
    capital_tier: (tiers[Math.min(2, tiers.length - 1)] || {}).label, additional_debt: 0,
    scale: label((o.scales || [])[Math.min(1, (o.scales || []).length - 1)]),
    difficulty: "Standard", scenario: "Custom Founding", challenge: null,
  };
  try {
    const res = await fetch(`${API}/company`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) throw new Error((await res.json()).detail || "Could not found company.");
    try { localStorage.setItem("ges_tour_seen", "1"); } catch (e) { /* the tour may show */ }
    $("onboarding-screen").classList.add("hidden");
    await enterDashboard();
    toast(`${payload.name} is open for business. Let's launch your first product.`);
    if (window.openLaunchProductModal) window.openLaunchProductModal({ fast: true });
  } catch (err) {
    toast(err.message, true);
    if (/already exists/i.test(err.message)) $("onboard-new-game").classList.remove("hidden");
  } finally {
    btn.disabled = false;
  }
}

$("qs-go").addEventListener("click", quickStart);
$("qs-demo")?.addEventListener("click", () => { if (typeof window.loadDemoCompany === "function") window.loadDemoCompany(); });

$("onboard-new-game").addEventListener("click", () => startNewGame("This deletes the current company and all its history. Start a brand new game?"));

export { openResetSaveModal, populateOnboardingDropdowns, startNewGame };
